import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import { ESymbolRoles } from '@Core/Reels/SymbolRoles';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { TSymbolIds } from '@GFTypes/GameStoreTypes';
import { IExpandingSymbolsConfiguration } from '@GFTypes/GameStoreTypes/ExpandingSymbolsConfiguration';
import { ELayeringOrder } from '@GFTypes/LayeringOrder';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { StateManager } from '@Managers/StateManager/StateManager';
import { GameScene, Reels } from '..';
import { ExpandingSymbol } from './ExpandingSymbol';
import {
    EXPANDING_SYMBOL_ROLES,
    EXPANDING_SYMBOL_STRATEGY_BY_ROLE_TYPE,
    expandingSymbolStateLeaveCondition,
    postExpandingSymbolStateLeaveCondition,
    subStates
} from './ExpandingSymbolFeature.constants';
import { EExpandingMode, EExpandingSkippingMode, SymbolExpansionStrategy } from './ExpandingSymbolFeature.types';
import { Container, Point } from 'pixi.js';
import { TimerEvent } from '@Core/Scene/Scene';

export class ExpandingSymbolFeature extends GameComponent {
    public winningExpandingSymbolIndex: number = 0;
    private readonly config: IExpandingSymbolsConfiguration;
    private readonly topLayerContainer: Container;
    private strategies: SymbolExpansionStrategy[];
    private expandingSymbols: ExpandingSymbol[] = [];
    private skipNext: boolean;
    private delayedCalls: [ExpandingSymbol, TimerEvent, (value: void | PromiseLike<void>) => void][] = [];
    private expandedSymbolIndex: number = 0;
    private mode: EExpandingMode = EExpandingMode.SIMULTANEOUS;
    private delay: number;

    constructor(
        public scene: GameScene,
        private readonly symbolsByRole: Map<ESymbolRoles, TSymbolIds>,
        private readonly reels: Reels
    ) {
        super(scene);
        const { expandingSymbols } = ConfigLoader.getConfiguration(EConfigurationNames.GAME);
        this.config = expandingSymbols ?? ({} as IExpandingSymbolsConfiguration);
        StateManager.assignSubStates(EGameStates.PRE_WIN_FEATURES_STATE, subStates);
        this.topLayerContainer = new Container({ x: 0, y: 0, parent: this.scene.stage });
        this.topLayerContainer.zIndex = this.config.zIndex ?? ELayeringOrder.EXPANDED_WILD_LAYER;
        this.scene.game.gameStore.set(
            EGameStoreKeys.BaseWinPresentationState,
            { winPresentationBeforeExpandingDuration: this.config.winPresentationBeforeExpandingDuration },
            true
        );

        EventsCenter.on(EGameStates.EXPANDING_SYMBOL_STATE, this.expandSymbols, this);
        EventsCenter.on(EGameStates.COMPLETE_WIN_STATE, this.onCompleteWinState, this);
        EventsCenter.on(EGameStates.POST_EXPANDING_SYMBOL_STATE, this.onPostExpandingSymbolState, this);
        EventsCenter.on(EGameEvents.RESIZE_SYMBOLS, this.onReelsResize, this);
        EventsCenter.on(EGameStates.PRE_WIN_STEP_STATE, this.preWinStepState, this);
        EventsCenter.on(EGameEvents.SKIP, this.onSkip, this);
    }

    public onCreate(): void {
        this.strategies = this.getStrategies(this.symbolsByRole);
        this.expandingSymbols = this.strategies.flatMap((strategy) =>
            strategy.getInitialExpandingSymbols(this.config.loopWinAnimation, this.config.showOnlyHighestMultiplier)
        );
        this.mode = this.config.mode ?? EExpandingMode.SIMULTANEOUS;
        this.delay = this.config.delayBetween ?? 0;

        if (this.mode === EExpandingMode.DELAYED && this.delay <= 0) this.mode = EExpandingMode.SIMULTANEOUS;
    }

    private preWinStepState() {
        this.winningExpandingSymbolIndex = 0;
        const outcome = this.gameStore.get(EGameStoreKeys.SpinOutcome);
        this.expandingSymbols.forEach((expandingSymbol) => (expandingSymbol.shouldExpand = false));
        const winningExpandingSymbols = this.strategies.flatMap((strategy) => strategy.getWinningExpandingSymbols(outcome));
        if (winningExpandingSymbols.length === 0) return;
        this.gameStore.set(EGameStoreKeys.ExpandingSymbolState, { isWinningExpandingSymbolPresent: true }, true);
        winningExpandingSymbols.forEach((winningExpandingSymbol) => {
            for (const expandingSymbol of this.expandingSymbols) {
                const winningExpandingSymbolInOriginPositions = expandingSymbol.originPositions.some(
                    (position) =>
                        position.reelIndex === winningExpandingSymbol.positionOnReel.reelIndex &&
                        position.visibleSymbolIndex === winningExpandingSymbol.positionOnReel.visibleSymbolIndex
                );
                if (winningExpandingSymbolInOriginPositions) {
                    if (expandingSymbol.id !== winningExpandingSymbol.symbolId)
                        expandingSymbol.changeSymbol(winningExpandingSymbol.symbolId, this.reels.symbolSpinePool);
                    expandingSymbol.shouldExpand = true;
                    expandingSymbol.winningExpandingSymbolIndex = this.winningExpandingSymbolIndex;
                    expandingSymbol.isLastWinningExpandingSymbol = this.winningExpandingSymbolIndex === winningExpandingSymbols.length - 1;
                    expandingSymbol.visibleRowIndex = winningExpandingSymbol.positionOnReel.visibleSymbolIndex;
                    this.winningExpandingSymbolIndex++;
                }
            }
        });
        const { currentWinPresentationStepIndex, winningPositions } = this.gameStore.get(EGameStoreKeys.BaseWinPresentationState);
        if (currentWinPresentationStepIndex === 0 && winningPositions.flat().some(Boolean)) EventsCenter.emit(EGameEvents.WIN_BEFORE_EXPANDING_SYMBOLS_SOUND);
    }

    private getStrategies(symbolsByRole: Map<ESymbolRoles, number[]>): SymbolExpansionStrategy[] {
        const strategies: SymbolExpansionStrategy[] = [];

        for (const [role, symbolIds] of symbolsByRole) {
            if (!EXPANDING_SYMBOL_ROLES.includes(role)) continue;
            const strategyFactory = EXPANDING_SYMBOL_STRATEGY_BY_ROLE_TYPE[role];
            if (!strategyFactory) continue;
            const strategy = strategyFactory(this.scene, this.topLayerContainer, this.reels.symbolSpinePool, symbolIds, this.config.isVisibleAfterExpanding);
            strategy.symbolIds.push(...symbolIds);
            strategies.push(strategy);
        }

        return strategies;
    }

    private async emitAndExpand(symbol: ExpandingSymbol): Promise<void> {
        EventsCenter.emit(EGameEvents.SYMBOL_EXPANDING_$, { eventIds: [++this.expandedSymbolIndex] });
        await symbol.expand(this.skipNext);
    }

    private async expandSymbols(): Promise<void> {
        this.expandedSymbolIndex = 0;
        this.skipNext = false;
        const symbolsToExpand = this.expandingSymbols.filter((exp) => exp.shouldExpand);

        switch (this.mode) {
            case EExpandingMode.SIMULTANEOUS: {
                await Promise.all(symbolsToExpand.map((symbol) => this.emitAndExpand(symbol)));
                break;
            }

            case EExpandingMode.DELAYED: {
                const expansionPromises = symbolsToExpand.map((symbol, index) => {
                    return new Promise<void>((resolve) => {
                        const timerEvent = this.scene.time.delayedCall(this.delay * index, async () => {
                            await this.emitAndExpand(symbol);
                            resolve();
                        });
                        this.delayedCalls.push([symbol, timerEvent, resolve]);
                    });
                });

                await Promise.all(expansionPromises);
                break;
            }

            case EExpandingMode.SEQUENTIAL: {
                for (const symbol of symbolsToExpand) {
                    await this.emitAndExpand(symbol);
                }
                break;
            }
        }

        this.delayedCalls = [];
        expandingSymbolStateLeaveCondition.resolve();
    }

    private onReelsResize(): void {
        const { reelsSpineScale } = this.gameStore.get(EGameStoreKeys.ReelParameters);
        if (!this.topLayerContainer) return;
        this.topLayerContainer.scale = reelsSpineScale;
        this.topLayerContainer.position = new Point(this.scene.screen.width / 2, this.scene.screen.height / 2);
    }

    private onPostExpandingSymbolState(): void {
        postExpandingSymbolStateLeaveCondition.resolve();
    }

    private onCompleteWinState(): void {
        this.gameStore.set(EGameStoreKeys.ExpandingSymbolState, { isWinningExpandingSymbolPresent: false }, true);
        this.expandingSymbols.forEach((expandingSymbol) => expandingSymbol.hide());
    }

    private onSkip(): void {
        const { expandingSymbols } = ConfigLoader.getConfiguration(EConfigurationNames.GAME);
        const { skipMode } = expandingSymbols ?? ({} as IExpandingSymbolsConfiguration);

        if (!(skipMode && this.gameStore.get(EGameStoreKeys.CurrentGameSubState) === EGameStates.EXPANDING_SYMBOL_STATE)) return;

        if (skipMode === EExpandingSkippingMode.ALL) this.skipAll();
        if (skipMode === EExpandingSkippingMode.SINGLE) this.skipCurrent();
    }

    private isSymbolNotExpandedYet(symbol: ExpandingSymbol): boolean {
        return symbol.shouldExpand && !symbol.expanded && symbol.spine.spine.visible;
    }

    private skipAll(): void {
        if (this.mode === EExpandingMode.SIMULTANEOUS) {
            this.expandingSymbols.filter(this.isSymbolNotExpandedYet).forEach((exp) => exp.skip());
        }

        if (this.mode === EExpandingMode.DELAYED) {
            this.delayedCalls.forEach(([symbol, timerEvent, resolve]) => {
                clearTimeout(timerEvent.timeoutId);
                resolve();
            });

            this.expandingSymbols
                .filter((symbol) => symbol.shouldExpand && !symbol.expanded)
                .forEach((exp, index) => {
                    const track = exp.spine.getCurrentAnimationTrack();

                    if (track?.animation?.name === exp.getExpandingAnimationName()) {
                        exp.skip();
                    } else {
                        this.emitAndExpand(exp);
                        exp.skip();
                    }
                });
        }

        if (this.mode === EExpandingMode.SEQUENTIAL) {
            const symbolToSkip = this.expandingSymbols.find(this.isSymbolNotExpandedYet);
            if (!symbolToSkip) return;
            symbolToSkip.skip();
            this.skipNext = true;
        }
    }

    private skipCurrent(): void {
        if (this.mode === EExpandingMode.SIMULTANEOUS || this.mode === EExpandingMode.SEQUENTIAL) {
            const symbolToSkip = this.expandingSymbols.find(this.isSymbolNotExpandedYet);
            if (!symbolToSkip) return;
            symbolToSkip.skip();
        }

        if (this.mode === EExpandingMode.DELAYED) {
            const currentDelayedCall = this.delayedCalls.find(([symbol]) => this.isSymbolNotExpandedYet(symbol));
            if (currentDelayedCall) {
                const [symbol, timerEvent, resolve] = currentDelayedCall;
                if (!timerEvent.hasDispatched) {
                    timerEvent.destroy();
                    resolve();
                }

                symbol.skip();
            }
        }
    }
}
